/* eslint-disable no-lonely-if */
/* eslint-disable max-len */
// Image processing utilities

import { SUPPORTED_IMAGE_FORMATS, MAX_DATA_URI_SIZE } from '../types';
import type { UploadResult, ImageValidationResult, ImageDimensions } from '../types';
import { extractFileTypeFromUrl, generateRandomId } from './commonHelpers';

// Cache for image type verification results
const imageTypeCache = new Map<string, {type: string, timestamp: number}>();

/**
 * Verifies image type using a HEAD request
 */
export const verifyImageTypeWithHEAD = async (url: string): Promise<string | null> =>
{
	// Check cache first (valid for 1 hour)
	const cached = imageTypeCache.get(url);
	const now = Date.now();

	if (cached && (now - cached.timestamp < 3600000))
	{
		return cached.type;
	}

	try
	{
		// Create AbortController for timeout
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

		const response = await fetch(url, {
			method : 'HEAD',
			mode   : 'cors',
			signal : controller.signal
		});

		clearTimeout(timeoutId);

		if (!response.ok)
		{
			return null;
		}

		const contentType = response.headers.get('Content-Type');

		if (!contentType)
		{
			return null;
		}

		// Parse the MIME type
		const mimeMatch = contentType.match(/^image\/([a-z0-9.+-]+)$/i);

		if (mimeMatch && mimeMatch[1])
		{
			const imageType = mimeMatch[1].toLowerCase();

			// Cache the result
			imageTypeCache.set(url, { type: imageType, timestamp: now });

			return imageType;
		}

		return null;
	}
	catch (error)
	{
		return null;
	}
};

/**
 * Estimates the approximate size of a data URI in bytes
 */
export const estimateDataUriSize = (dataUri: string): number =>
{
	if (!dataUri.includes(';base64,'))
	{
		return dataUri.length;
	}

	// For base64 data URIs
	const base64Data = dataUri.split(';base64,')[1];

	if (!base64Data) return 0;

	// 4 base64 chars represent 3 bytes of data
	// Account for padding characters
	const paddingCount = (base64Data.match(/[=]/g) || []).length;

	return Math.floor((base64Data.length * 3) / 4) - paddingCount;
};

/**
 * Gets image dimensions from a data URI
 */
export const getDataUriDimensions = (dataUri: string): Promise<ImageDimensions> =>
{
	return new Promise((resolve, reject) =>
	{
		const img = new Image();

		img.onload = () =>
		{
			resolve({
				width  : img.naturalWidth,
				height : img.naturalHeight
			});
		};
		img.onerror = () =>
		{
			reject(new Error('Failed to load image from data URI'));
		};
		img.src = dataUri;
	});
};

/**
 * Validates a data URI for format correctness and size limits
 */
export const validateDataUri = (dataUri: string): ImageValidationResult =>
{
	// Check basic format
	if (!dataUri.startsWith('data:image/'))
	{
		return { valid: false, reason: 'Not an image data URI' };
	}

	// Check size
	const size = estimateDataUriSize(dataUri);

	if (size > MAX_DATA_URI_SIZE)
	{
		return {
			valid  : false,
			reason : `Image too large (${(size / (1024 * 1024)).toFixed(2)} MB). Maximum size is 5MB.`
		};
	}

	// If it's base64 encoded, check for valid base64 format
	if (dataUri.includes(';base64,'))
	{
		const base64Data = dataUri.split(';base64,')[1];

		if (!base64Data || !/^[A-Za-z0-9+/]*={0,2}$/.test(base64Data))
		{
			return { valid: false, reason: 'Invalid base64 encoding' };
		}
	}

	return { valid: true };
};

/**
 * Processes an image URL to extract metadata
 */
export const processImageUrl = async (url: string): Promise<UploadResult | null> =>
{
	// Handle data URIs specially
	if (url.startsWith('data:image/'))
	{
		// Validate data URI
		const validation = validateDataUri(url);

		if (!validation.valid)
		{
			throw new Error(validation.reason);
		}

		// Extract file type or default to png
		let fileType = 'png';
		const matches = url.match(/^data:image\/([a-z0-9.+-]+);base64,/i);

		if (matches && matches.length > 1)
		{
			fileType = matches[1].toLowerCase();
		}

		// Estimate size
		const sizeInBytes = estimateDataUriSize(url);

		// Try to get dimensions
		let dimensions;

		try
		{
			dimensions = await getDataUriDimensions(url);
		}
		catch (error)
		{
			// Use default dimensions if unable to determine
		}

		// Create a filename-friendly title for data URI
		const title = `image-${generateRandomId()}`;

		const result: UploadResult = {
			url,
			source         : 'embed_link', // Keeping source as 'embed_link' as requested
			thumbnail      : url,
			fileType,
			title,
			platform       : 'data_uri_image',
			readyForUpload : true,
			// Format metadata in a way that can be properly processed by server
			metadata       : {
				isDataUri   : true,
				sizeInBytes : sizeInBytes || 0,
				dimensions  : dimensions || { width: 800, height: 600 }
			}
		};

		return result;
	}

	// For regular image URLs:

	// 1. First try to get file type from URL pattern
	let fileType = extractFileTypeFromUrl(url) || '';

	// 2. For URLs without clear extensions or Google complex URLs, try HEAD request
	if (!fileType || (
		(url.includes('encrypted-tbn') ||
		url.includes('googleusercontent.com') ||
		(url.includes('google.com') && (url.includes('&q=') || url.includes('=w'))))
	))
	{
		try
		{
			const verifiedType = await verifyImageTypeWithHEAD(url);

			if (verifiedType)
			{
				// fileType = verifiedType;
			}
		}
		catch (error)
		{
			// Continue with existing type or fallback
		}
	}

	// 3. If we still don't have a type, use fallback for Google images
	if (!fileType)
	{
		if (url.includes('googleusercontent.com') ||
			url.includes('encrypted-tbn') ||
			url.includes('google.com'))
		{
			fileType = 'jpg'; // Default fallback for Google images
		}
		else
		{
			// For other URLs, try to determine from common image format patterns
			if (SUPPORTED_IMAGE_FORMATS.some((ext) => url.toLowerCase().includes(ext)))
			{
				for (const ext of SUPPORTED_IMAGE_FORMATS)
				{
					if (url.toLowerCase().includes(ext))
					{
						fileType = ext;
						break;
					}
				}
			}
			else
			{
				fileType = 'jpg'; // Default fallback for all other images
			}
		}
	}

	// Check if we have a supported file type
	if (!SUPPORTED_IMAGE_FORMATS.includes(fileType))
	{
		// Try to convert to supported type if possible
		fileType = fileType === 'jpeg' ? 'jpg' : 'jpg';
	}

	// Create a filename-friendly title
	const title = `image-${generateRandomId()}`;

	// Try to get image dimensions
	let dimensions;

	try
	{
		dimensions = await getDataUriDimensions(url);
	}
	catch (error)
	{
		dimensions = { width: 800, height: 600 };
	}

	const result = {
		url,
		source         : 'embed_link', // Ensuring consistent source value
		thumbnail      : url, // Use the image URL itself as the thumbnail
		fileType,
		title,
		platform       : 'image_url',
		readyForUpload : true,
		metadata       : {
			isDataUri : false,
			dimensions
		}
	};

	return result;
};